// src/modules/units/tyres/views/retread-batch/retreadbatchform.view.js
//
// Pure rendering for the create / edit DRAFT form — supplier, expected return
// date, and notes. Data in, HTML out; no fetch, no DOM. dispatchReference is
// deliberately NOT here: it's a dispatch-time field (the delivery note when the
// batch actually leaves), so it belongs to the dispatch step, not draft setup.
//
// Reuses the shared form primitives (tm-form / tm-form-grid / tm-form-label /
// tm-input / tm-textarea / tm-form-actions), so it matches the tyre capture forms.

import { esc } from '../../shared/tyre-format.js';

// ISO timestamp -> <input type="date"> value (YYYY-MM-DD), local. Blank for a
// missing/instringable date so the field just starts empty.
function dateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function supplierOptions(suppliers, selected) {
  const opts = [`<option value="">Select a supplier…</option>`];
  for (const s of suppliers || []) {
    const id = s?.supplierId;
    if (id === undefined || id === null) continue;
    const sel = String(id) === String(selected) ? ' selected' : '';
    opts.push(`<option value="${esc(id)}"${sel}>${esc(s.supplierName || 'Unnamed supplier')}</option>`);
  }
  return opts.join('');
}

// A readable label for a catalogue entry: "Bandag BDR · pattern · size".
export function catalogueLabel(entry = {}) {
  const name = [entry.manufacturerName, entry.modelName].filter(Boolean).join(' · ').trim();
  const bits = [name || 'Unnamed product', entry.treadPatternCode, entry.tyreSize].filter(Boolean);
  return bits.join(' · ');
}

const catalogueEntryId = (e = {}) => e.tyreCatalogueEntryId ?? e.id ?? e._id ?? null;

function catalogueOptions(catalogue, selected) {
  const opts = [`<option value="">Select a retread product…</option>`];
  for (const e of catalogue || []) {
    const id = catalogueEntryId(e);
    if (id === undefined || id === null) continue;
    const sel = String(id) === String(selected) ? ' selected' : '';
    opts.push(`<option value="${esc(id)}"${sel}>${esc(catalogueLabel(e))}</option>`);
  }
  return opts.join('');
}

/**
 * @param {object} p
 * @param {'create'|'edit'} p.mode
 * @param {object} [p.batch]     the batch being edited (edit mode)
 * @param {Array}  [p.suppliers] supplier rows for the picker
 * @param {Array}  [p.catalogue] active catalogue entries for the retread-product picker (create mode)
 */
export function batchFormHTML({ mode = 'create', batch = {}, suppliers = [], catalogue = [] } = {}) {
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit retread batch' : 'New retread batch';
  const ref = isEdit && batch?.reference ? esc(batch.reference) : '';
  const sub = isEdit
    ? `Update the batch's details${ref ? ` — <b>${ref}</b>` : ''}. Supplier can only change while it's a draft.`
    : `Create a draft consignment. Pick the retread product and supplier, then add tyres and dispatch.`;

  const supplierId = isEdit ? batch?.supplierId : '';
  const expReturn = dateInputValue(isEdit ? batch?.expectedReturnDate : '');
  const notes = isEdit ? (batch?.notes || '') : '';

  // The retread product (which catalogue entry the tyres will become) is chosen at
  // creation and fixed thereafter — it's shown read-only on the detail, not offered
  // on the edit form. So this row only appears when creating.
  const productRow = isEdit ? '' : `
        <div class="tm-form-row tm-form-row-wide">
          <label class="tm-form-label" for="tm-rb-catalogue">Retread product <span class="tm-req">*</span></label>
          <select id="tm-rb-catalogue" class="tm-input" data-rb-field="tyreCatalogueEntryId">
            ${catalogueOptions(catalogue, '')}
          </select>
          <p class="tm-field-help">The retread the whole batch will undergo — separate from who does the work.</p>
        </div>`;

  return `
    <div class="tm-form tm-rb-form">
      <div class="tm-form-head">
        <div>
          <h2 class="tm-form-title">${esc(title)}</h2>
          <p class="tm-form-sub">${sub}</p>
        </div>
      </div>

      <div class="tm-form-msg" data-rb-msg hidden></div>

      <div class="tm-form-grid">
        ${productRow}
        <div class="tm-form-row">
          <label class="tm-form-label" for="tm-rb-supplier">Supplier <span class="tm-req">*</span></label>
          <select id="tm-rb-supplier" class="tm-input" data-rb-field="supplierId">
            ${supplierOptions(suppliers, supplierId)}
          </select>
        </div>

        <div class="tm-form-row">
          <label class="tm-form-label" for="tm-rb-return">Expected return date</label>
          <input id="tm-rb-return" type="date" class="tm-input" data-rb-field="expectedReturnDate" value="${esc(expReturn)}" />
          <p class="tm-field-help">Optional — when you expect the tyres back from the retreader.</p>
        </div>

        <div class="tm-form-row tm-form-row-wide">
          <label class="tm-form-label" for="tm-rb-notes">Notes</label>
          <textarea id="tm-rb-notes" class="tm-input tm-textarea" rows="3" data-rb-field="notes" placeholder="Anything the depot should know about this consignment.">${esc(notes)}</textarea>
        </div>
      </div>

      <div class="tm-form-actions">
        <button type="button" class="tm-btn-secondary" data-rb-cancel>Cancel</button>
        <button type="button" class="tm-btn-primary" data-rb-save>${isEdit ? 'Save changes' : 'Create batch'}</button>
      </div>
    </div>`;
}

// Standalone state (loading suppliers / no suppliers / load error), shown in place
// of the form by the orchestrator.
export function formStateHTML(kind, message, { retry = false, addSupplier = false } = {}) {
  const retryBtn = retry ? ` <button type="button" class="tm-btn-link" data-rb-retry>Retry</button>` : '';
  const addBtn = addSupplier ? ` <button type="button" class="tm-btn-link" data-rb-add-supplier>Add a supplier</button>` : '';
  return `<div class="tm-rb-state"><span class="tm-state"><span class="tm-state-msg">${esc(message)}${retryBtn}${addBtn}</span></span></div>`;
}
